import React, { useEffect, useState } from 'react';
import { Button, Modal, Select } from 'antd';

import { useOptionalFavorites } from '../../contexts/FavoritesContext';
import { RECORD_LIST_COUNT } from '../../services/favorites';

/** The counts a list may show, as Select options ("5 records"). */
export const RECORD_LIST_COUNT_OPTIONS = Array.from(
	{ length: RECORD_LIST_COUNT.MAX - RECORD_LIST_COUNT.MIN + 1 },
	(_, i) => RECORD_LIST_COUNT.MIN + i
).map(n => ({ value: n, label: `${n} records` }));

/**
 * "Pin as a list" (KPR-1128): how many of a card's records — newest first,
 * or under the saved filter in `target` in its own order — a list on Home
 * shows, 3 to 10 (5 at first). Opened on a list that is already pinned, it
 * shows its count, saves the change and offers Unpin. The records are read on
 * Home each time it loads.
 *
 * `target`: `{ targetId (the card), collectionId, savedFilterId }`. `name`
 * says what is pinned ("Readings", "North ward").
 */
export default function RecordListModal({ open, onClose, target, name }) {
	const favorites = useOptionalFavorites();
	const current = open && favorites?.listFor ? favorites.listFor(target) : null;
	const existing = Number.isInteger(current);
	const [count, setCount] = useState(RECORD_LIST_COUNT.DEFAULT);
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		if (open) setCount(existing ? current : RECORD_LIST_COUNT.DEFAULT);
	}, [open, existing, current]);

	const save = async () => {
		if (!favorites?.saveList) return;
		setBusy(true);
		const ok = await favorites.saveList(target, count);
		setBusy(false);
		if (ok) onClose();
	};
	const unpin = async () => {
		if (!favorites?.removeList) return;
		setBusy(true);
		const ok = await favorites.removeList(target);
		setBusy(false);
		if (ok) onClose();
	};

	return (
		<Modal
			open={open}
			title={existing ? 'Edit Home list' : 'Pin as a list'}
			onCancel={onClose}
			destroyOnHidden
			className="record-tile-modal record-list-modal"
			footer={(
				<div className="record-tile-modal__footer">
					{existing ? (
						<Button type="link" onClick={unpin} disabled={busy}>Unpin from Home</Button>
					) : <span />}
					<span className="record-tile-modal__actions">
						<Button onClick={onClose} disabled={busy}>Cancel</Button>
						<Button type="primary" onClick={save} loading={busy}>
							{existing ? 'Save' : 'Pin to Home'}
						</Button>
					</span>
				</div>
			)}
		>
			<p className="record-tile-modal__lead">
				{name ? <strong>{name}</strong> : null}
				{name ? ' — ' : null}
				Show the first records on your Home. They are read each time Home opens.
			</p>
			<label className="record-list-modal__field">
				<span className="micro-label">How many</span>
				<Select
					className="record-list-modal__count"
					aria-label="How many records"
					value={count}
					onChange={setCount}
					options={RECORD_LIST_COUNT_OPTIONS}
				/>
			</label>
		</Modal>
	);
}
